import React, { useState, useEffect } from 'react';
import {
    getUserPermissions,
    updateUserPermissions,
    getProjects,
    Project,
    User,
} from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassBadge } from '../ui/GlassBadge';
import {
    Home,
    Palette,
    MessageSquare,
    Rocket,
    HelpCircle,
    Film,
    Globe,
    Sparkles,
    TrendingUp,
    Users,
    CalendarDays,
    Megaphone,
    Search,
    BarChart2,
    Share2,
    UserPlus,
    Image,
    Star,
    X,
    Mail,
    Plug,
    ShoppingBag,
    Video,
    CreditCard,
    PieChart,
    RefreshCw,
} from 'lucide-react';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

const ALL_MODULES = [
    // Trial tier (available to all)
    { key: 'dashboard', label: 'Dashboard', icon: Home, tier: 'trial' },
    { key: 'brand', label: 'Brand Portal', icon: Palette, tier: 'trial' },
    { key: 'content', label: 'Creator & Content', icon: MessageSquare, tier: 'trial' },
    { key: 'campaigns', label: 'Campaigns', icon: Megaphone, tier: 'trial' },
    { key: 'edm', label: 'Email Marketing', icon: Mail, tier: 'trial' },
    { key: 'sms', label: 'SMS', icon: MessageSquare, tier: 'trial' },
    { key: 'multichannel', label: 'Multi-Channel', icon: Share2, tier: 'trial' },
    { key: 'seo', label: 'SEO', icon: Search, tier: 'trial' },
    { key: 'analytics', label: 'Analytics', icon: BarChart2, tier: 'trial' },
    { key: 'landing_pages', label: 'Landing Pages', icon: Rocket, tier: 'trial' },
    { key: 'lead_capture', label: 'Lead Capture', icon: UserPlus, tier: 'trial' },
    { key: 'quizzes', label: 'Quizzes', icon: HelpCircle, tier: 'trial' },
    { key: 'scripts', label: 'Scripts', icon: Film, tier: 'trial' },
    { key: 'scheduling', label: 'Scheduling', icon: CalendarDays, tier: 'trial' },
    { key: 'images', label: 'Images', icon: Image, tier: 'trial' },
    { key: 'reviews', label: 'Reviews', icon: Star, tier: 'trial' },
    { key: 'websites', label: 'Websites', icon: Globe, tier: 'trial' },
    { key: 'websites_v2', label: 'Site Builder', icon: Sparkles, tier: 'trial' },
    { key: 'wordpress', label: 'WordPress', icon: Globe, tier: 'trial' },
    { key: 'leads_crm', label: 'Leads / CRM', icon: TrendingUp, tier: 'trial' },
    { key: 'integrations', label: 'Integrations', icon: Plug, tier: 'trial' },
    { key: 'products', label: 'Products', icon: ShoppingBag, tier: 'trial' },
    { key: 'video', label: 'Video', icon: Video, tier: 'trial' },
    // Pro tier
    { key: 'domains', label: 'Domains', icon: Globe, tier: 'pro' },
    { key: 'products_subscriptions', label: 'Product Subscriptions', icon: CreditCard, tier: 'pro' },
    { key: 'products_analytics', label: 'Product Analytics', icon: PieChart, tier: 'pro' },
    // Enterprise tier
    { key: 'teams', label: 'My Teams', icon: Users, tier: 'enterprise' },
    { key: 'products_marketing_loop', label: 'Marketing Loop', icon: RefreshCw, tier: 'enterprise' },
];

// Explicit, superadmin-granted opt-in features — intentionally separate from
// ALL_MODULES/allowed_modules. These are OFF for every account by default,
// regardless of whether the account's Module Access is restricted or "all
// allowed" (see authorization.require_feature_flag). Add future filters here.
const ALL_FEATURE_FLAGS = [
    { key: 'noise_filter', label: 'Noise Filter (Images)', icon: Sparkles },
];

interface UserPermissionsPanelProps {
    user: User;
    onClose: () => void;
}

const UserPermissionsPanel: React.FC<UserPermissionsPanelProps> = ({ user, onClose }) => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedModules, setSelectedModules] = useState<string[] | null>(null);
    const [selectedProjects, setSelectedProjects] = useState<string[] | null>(null);
    const [selectedFeatureFlags, setSelectedFeatureFlags] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // "null" means "all allowed" (no restrictions); we use a checkbox "restrict" toggle
    const [restrictModules, setRestrictModules] = useState(false);
    const [restrictProjects, setRestrictProjects] = useState(false);

    useEffect(() => {
        loadData();
    }, [user.user_id]);

    const loadData = async () => {
        setIsLoading(true);
        setError('');
        try {
            const [perms, projs] = await Promise.all([
                getUserPermissions(user.user_id),
                getProjects(),
            ]);
            setProjects(projs);

            if (perms.allowed_modules !== null) {
                setRestrictModules(true);
                setSelectedModules(perms.allowed_modules);
            } else {
                setRestrictModules(false);
                setSelectedModules(null);
            }

            if (perms.allowed_projects !== null) {
                setRestrictProjects(true);
                setSelectedProjects(perms.allowed_projects);
            } else {
                setRestrictProjects(false);
                setSelectedProjects(null);
            }

            setSelectedFeatureFlags(perms.feature_flags ?? []);
        } catch (err: any) {
            setError('Failed to load permissions');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleModule = (key: string) => {
        setSelectedModules(prev => {
            const current = prev ?? [];
            if (current.includes(key)) {
                return current.filter(k => k !== key);
            }
            return [...current, key];
        });
    };

    const toggleFeatureFlag = (key: string) => {
        setSelectedFeatureFlags(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const toggleProject = (projectId: string) => {
        setSelectedProjects(prev => {
            const current = prev ?? [];
            if (current.includes(projectId)) {
                return current.filter(id => id !== projectId);
            }
            return [...current, projectId];
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError('');
        setSuccessMsg('');
        try {
            await updateUserPermissions(user.user_id, {
                allowed_modules: restrictModules ? (selectedModules ?? []) : null,
                allowed_projects: restrictProjects ? (selectedProjects ?? []) : null,
                feature_flags: selectedFeatureFlags,
            });
            setSuccessMsg('Permissions saved successfully');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to save permissions'));
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <GlassCard variant="default" padding="lg" className="permissions-panel">
                <div className="permissions-loading">Loading permissions...</div>
            </GlassCard>
        );
    }

    return (
        <GlassCard variant="default" padding="lg" className="permissions-panel">
            <div className="permissions-header">
                <h3>Permissions: {user.full_name || user.username}</h3>
                <span className="permissions-email">{user.email}</span>
                <button className="permissions-close" onClick={onClose}>
                    <X size={18} />
                </button>
            </div>

            {error && <div className="permissions-error">{error}</div>}
            {successMsg && <div className="permissions-success">{successMsg}</div>}

            {/* Module Access */}
            <GlassCard variant="strong" padding="md" className="permissions-section">
                <div className="permissions-section-header">
                    <strong>Module Access</strong>
                    <label className="toggle-switch small">
                        <input
                            type="checkbox"
                            checked={restrictModules}
                            onChange={e => {
                                setRestrictModules(e.target.checked);
                                if (e.target.checked && selectedModules === null) {
                                    setSelectedModules([]);
                                }
                            }}
                        />
                        <span className="toggle-slider"></span>
                    </label>
                    <GlassBadge variant={restrictModules ? 'warning' : 'success'} size="sm">
                        {restrictModules ? 'Restricted' : 'All allowed'}
                    </GlassBadge>
                </div>
                {restrictModules && (
                    <div className="permissions-modules-grid">
                        {ALL_MODULES.map(mod => {
                            const IconComponent = mod.icon;
                            return (
                                <label key={mod.key} className="permissions-module-item">
                                    <input
                                        type="checkbox"
                                        checked={(selectedModules ?? []).includes(mod.key)}
                                        onChange={() => toggleModule(mod.key)}
                                    />
                                    <span className="permissions-module-icon">
                                        <IconComponent size={16} />
                                    </span>
                                    <span className="permissions-module-label-col">
                                        <span>{mod.label}</span>
                                        {mod.tier !== 'trial' && (
                                            <span className={`permissions-tier-badge tier-${mod.tier}`}>
                                                {mod.tier}
                                            </span>
                                        )}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                )}
            </GlassCard>

            {/* Feature Access — explicit opt-in flags, independent of Module Access restriction state */}
            <GlassCard variant="strong" padding="md" className="permissions-section">
                <div className="permissions-section-header">
                    <strong>Feature Access</strong>
                    <GlassBadge variant="default" size="sm">Experimental</GlassBadge>
                </div>
                <div className="permissions-modules-grid">
                    {ALL_FEATURE_FLAGS.map(flag => {
                        const IconComponent = flag.icon;
                        return (
                            <label key={flag.key} className="permissions-module-item">
                                <input
                                    type="checkbox"
                                    checked={selectedFeatureFlags.includes(flag.key)}
                                    onChange={() => toggleFeatureFlag(flag.key)}
                                />
                                <span className="permissions-module-icon">
                                    <IconComponent size={16} />
                                </span>
                                <span>{flag.label}</span>
                            </label>
                        );
                    })}
                </div>
            </GlassCard>

            {/* Project Access */}
            <GlassCard variant="strong" padding="md" className="permissions-section">
                <div className="permissions-section-header">
                    <strong>Project Access</strong>
                    <label className="toggle-switch small">
                        <input
                            type="checkbox"
                            checked={restrictProjects}
                            onChange={e => {
                                setRestrictProjects(e.target.checked);
                                if (e.target.checked && selectedProjects === null) {
                                    setSelectedProjects([]);
                                }
                            }}
                        />
                        <span className="toggle-slider"></span>
                    </label>
                    <GlassBadge variant={restrictProjects ? 'warning' : 'success'} size="sm">
                        {restrictProjects ? 'Restricted' : 'All allowed'}
                    </GlassBadge>
                </div>
                {restrictProjects && (
                    <div className="permissions-projects-list">
                        {projects.filter(p => !p.is_archived).map(proj => (
                            <label key={proj.project_id} className="permissions-project-item">
                                <input
                                    type="checkbox"
                                    checked={(selectedProjects ?? []).includes(proj.project_id)}
                                    onChange={() => toggleProject(proj.project_id)}
                                />
                                <span>{proj.name}</span>
                            </label>
                        ))}
                        {projects.filter(p => !p.is_archived).length === 0 && (
                            <span className="permissions-empty">No projects found</span>
                        )}
                    </div>
                )}
            </GlassCard>

            <div className="permissions-actions">
                <GlassButton variant="ghost" onClick={onClose}>
                    Cancel
                </GlassButton>
                <GlassButton
                    variant="primary"
                    onClick={handleSave}
                    loading={isSaving}
                    disabled={isSaving}
                >
                    Save Permissions
                </GlassButton>
            </div>
        </GlassCard>
    );
};

export default UserPermissionsPanel;
